import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../hooks/useLanguage';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import BookingModal from '../components/BookingModal';
import { Calendar, Users, Clock, MapPin, Star, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { isOfferInlineBookingHost } from '../lib/offerInlineBooking';
import { getDisplayTtcEuros } from '../lib/offerPrice';
import SeoHead from '../components/SeoHead';
import GeoSummary from '../components/GeoSummary';
import OptimizedImage from '../components/OptimizedImage';
import getOfferCanonicalPath from '../lib/offerCanonicalPath';
import { buildBreadcrumbSchema, buildOfferProductSchema } from '../lib/seoSchema';

const OfferDetailPage = () => {
  const { id, slug, category } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [reviewStats, setReviewStats] = useState({ rating: 5.0, review_count: 0 });
  const [customTranslations, setCustomTranslations] = useState({});
  const [bookingSideExpanded, setBookingSideExpanded] = useState(false);
  const [bundleVariantIndex, setBundleVariantIndex] = useState(0);
  const [notFound, setNotFound] = useState(false);

  const embedInlineBooking = useMemo(() => isOfferInlineBookingHost(), []);
  const handleBookingFormInteract = useCallback(() => {
    setBookingSideExpanded(true);
  }, []);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const loadOfferDetails = async () => {
    setNotFound(false);
    setLoading(true);
    try {
      let offerUrl;

      if (id) {
        offerUrl = `${API_URL}/api/wowbooking/offers/${id}`;
      } else if (category && slug) {
        offerUrl = `${API_URL}/api/wowbooking/slug/${category}/${slug}`;
      } else if (slug) {
        offerUrl = `${API_URL}/api/wowbooking/slug/${slug}`;
      } else {
        throw new Error('No valid offer identifier provided');
      }

      const [offerResponse, reviewsResponse] = await Promise.all([
        axios.get(offerUrl),
        axios.get(`${API_URL}/api/google-reviews-stats`).catch(() => ({ data: { rating: 5.0, review_count: 0 } })),
      ]);

      setOffer(offerResponse.data);
      setReviewStats(reviewsResponse.data);
    } catch (error) {
      console.error('Failed to load offer details:', error);
      if (error?.response?.status === 404) {
        setNotFound(true);
      }
      setOffer(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOfferDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on route/language change
  }, [id, slug, category, language]);

  useEffect(() => {
    if (loading || !offer) return;
    const canonicalPath = getOfferCanonicalPath(offer);
    if (!canonicalPath) return;
    const currentPath = location.pathname.replace(/\/+$/, '') || '/';
    const targetPath = canonicalPath.replace(/\/+$/, '') || '/';
    if (currentPath !== targetPath) {
      navigate(`${targetPath}${location.search}${location.hash}`, { replace: true });
    }
  }, [loading, offer, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    if (!offer || loading) return;
    if (offer.is_bundle) {
      const ix = Math.min(
        Math.max(0, offer.default_variant_index ?? 0),
        (offer.variants?.length || 1) - 1
      );
      setBundleVariantIndex(ix);
    } else {
      setBundleVariantIndex(0);
    }
  }, [offer?.bundle_slug, offer?.is_bundle, loading]);

  useEffect(() => {
    if (!offer || loading) return;
    const v =
      offer.is_bundle && Array.isArray(offer.variants) && offer.variants.length > 0
        ? offer.variants[Math.min(bundleVariantIndex, offer.variants.length - 1)]
        : offer;
    const oid = v?.id;
    if (oid == null) return;
    let cancelled = false;
    (async () => {
      try {
        const [galleryResponse, transResponse] = await Promise.all([
          axios.get(`${API_URL}/api/wowbooking/offers/${oid}/gallery`).catch(() => ({ data: { images: [] } })),
          axios.get(`${API_URL}/api/offer-translations/${oid}`).catch(() => ({ data: { translations: {} } })),
        ]);
        if (cancelled) return;
        setGalleryImages(galleryResponse.data.images || []);
        setCustomTranslations(transResponse.data.translations || {});
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offer, bundleVariantIndex, loading, API_URL]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('booking') === '1' && !loading && offer) {
      if (embedInlineBooking) {
        setBookingSideExpanded(true);
        requestAnimationFrame(() => {
          document.getElementById('offer-booking-flow')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      } else {
        setIsBookingModalOpen(true);
      }
    }
  }, [location.search, loading, offer, embedInlineBooking]);

  useLayoutEffect(() => {
    if (loading || !offer) return;
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    const run = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    run();
    requestAnimationFrame(run);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- offer identity changes often; offer id + route keys are enough
  }, [loading, offer?.id, offer?.bundle_slug, bundleVariantIndex, id, slug, category]);

  const formatDuration = (minutes) => {
    if (!minutes) return '1 hour';
    if (minutes < 60) return `${minutes} ${t('offerCard.minutes')}`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return hours === 1 ? `1 ${t('offerCard.hour')}` : `${hours} ${t('offerCard.hours')}`;
    }
    return `${hours}h${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!offer || notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <SeoHead title="Offer not found | Wownautic" noindex canonicalPath={location.pathname} />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Offer not found</h1>
          <Button asChild>
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Use custom translation if available, otherwise fall back to WowBooking default
  const customTitle = customTranslations?.title?.[language];
  const customDesc = customTranslations?.description?.[language];
  
  const displayTitle = customTitle || offer.title || '';
  const description = customDesc || (typeof offer.description === 'object' 
    ? offer.description[language] || offer.description.fr || offer.description.en || ''
    : offer.description || '');

  const isBundle = offer.is_bundle === true;
  const effectiveOffer =
    isBundle && Array.isArray(offer.variants) && offer.variants.length > 0
      ? offer.variants[Math.min(bundleVariantIndex, offer.variants.length - 1)]
      : offer;
  const pricingMode = String(effectiveOffer?.pricing_mode || '').toLowerCase();
  const isPerPersonPricing = pricingMode === 'per_person';

  const images =
    galleryImages.length > 0
      ? galleryImages.map((img) => img.url)
      : effectiveOffer?.featured_image_url || offer.featured_image_url
        ? [effectiveOffer?.featured_image_url || offer.featured_image_url]
        : ['https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=1200&q=80'];

  const canonicalPath = getOfferCanonicalPath(offer) || location.pathname;

  const seoDescription = description
    ? `${description} Fuengirola, Costa del Sol.`
    : `${displayTitle} — book online at Wownautic, Fuengirola Marina.`;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const ogImage = images[0];
  const offerSchema = origin
    ? buildOfferProductSchema({
        origin,
        canonicalPath,
        title: displayTitle,
        description: seoDescription,
        image: ogImage,
        priceEuros: getDisplayTtcEuros(effectiveOffer),
        reviewStats,
        offerType: effectiveOffer?.offer_type,
        durationMinutes: Number(effectiveOffer?.duration_minutes ?? effectiveOffer?.duration) || 0,
      })
    : null;
  const breadcrumbSchema = origin
    ? buildBreadcrumbSchema(origin, [
        { name: 'Home', path: '/' },
        { name: 'Offers', path: '/#offers' },
        { name: displayTitle, path: canonicalPath },
      ])
    : null;
  const jsonLd = [offerSchema, breadcrumbSchema].filter(Boolean);

  const durationLabel =
    Number(effectiveOffer.duration_minutes ?? effectiveOffer.duration) > 0
      ? formatDuration(Number(effectiveOffer.duration_minutes ?? effectiveOffer.duration))
      : '—';

  const geoOfferText = t('geo.offerSummary', {
    title: displayTitle,
    duration: durationLabel,
    price: getDisplayTtcEuros(effectiveOffer),
    capacity: effectiveOffer.max_capacity || 2,
  });

  const inclusionKeys = ['captain', 'safety', 'drinks', 'insurance'];
  const imageAlt = (index) =>
    t('offer.imageAlt', { title: displayTitle, index: index + 1 });

  return (
    <div className="font-sans antialiased text-gray-900 bg-white">
      <SeoHead
        title={`${displayTitle} | Wownautic`}
        description={seoDescription}
        canonicalPath={canonicalPath}
        ogImage={ogImage}
        ogType="product"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="pt-32">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex text-sm text-gray-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/#offers" className="hover:text-primary-600">Offers</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{displayTitle}</span>
          </nav>
        </div>

        {/* Title */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <GeoSummary>{geoOfferText}</GeoSummary>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{displayTitle}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>Fuengirola, Costa del Sol</span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold">{reviewStats.rating.toFixed(1)}</span>
              <span>({reviewStats.review_count} reviews)</span>
            </div>
          </div>
        </div>

        {isBundle && offer.variants?.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
            <p className="text-sm font-medium text-gray-600 mb-3">{t('offerCard.selectDuration')}</p>
            <div className="flex flex-wrap gap-2">
              {offer.variants.map((v, idx) => {
                const minutes = v.duration_minutes ?? v.duration;
                const hasDur = minutes != null && Number(minutes) > 0;
                const label = hasDur
                  ? formatDuration(Number(minutes))
                  : v.title
                    ? `${String(v.title).slice(0, 36)}${String(v.title).length > 36 ? '…' : ''}`
                    : `Option ${idx + 1}`;
                return (
                  <button
                    key={v.id ?? idx}
                    type="button"
                    onClick={() => setBundleVariantIndex(idx)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${
                      idx === bundleVariantIndex
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-gray-200 bg-white text-gray-800 hover:border-primary-400'
                    }`}
                  >
                    <span className="block max-w-[14rem] truncate">{label}</span>
                    {hasDur ? (
                      <span className="mt-0.5 block text-xs font-normal opacity-90">
                        €{getDisplayTtcEuros(v)}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Two-column layout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">

          {/* Image Gallery — visible on MOBILE only (above booking card) */}
          <div className="mb-6 lg:hidden">
            <div
              className="flex gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {images.map((img, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 snap-center rounded-xl overflow-hidden"
                  style={{ width: images.length === 1 ? '100%' : '85vw' }}
                >
                  <OptimizedImage
                    src={img}
                    alt={imageAlt(idx)}
                    width={800}
                    height={260}
                    loading={idx === 0 ? 'eager' : 'lazy'}
                    fetchPriority={idx === 0 ? 'high' : undefined}
                    className="w-full object-cover"
                    style={{ height: '260px' }}
                  />
                </div>
              ))}
            </div>
            {images.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-3">
                {images.map((_, idx) => (
                  <div
                    key={idx}
                    className={`rounded-full transition-all duration-300 ${idx === selectedImage ? 'w-4 h-2 bg-primary-950' : 'w-2 h-2 bg-gray-300'}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-8 items-start">

            {/* LEFT COLUMN — scrollable (gallery on desktop + content) */}
            <div
              className={`w-full min-w-0 order-2 lg:order-1 transition-all duration-500 ease-out ${
                embedInlineBooking && bookingSideExpanded ? 'lg:w-1/2' : 'lg:w-2/3'
              }`}
            >

              {/* Image Gallery — DESKTOP only */}
              <div className="hidden lg:block mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded-xl overflow-hidden">
                  <div className="md:row-span-2">
                    <OptimizedImage
                      src={images[selectedImage]}
                      alt={imageAlt(selectedImage)}
                      width={800}
                      height={500}
                      loading="eager"
                      fetchPriority="high"
                      className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ minHeight: '300px', maxHeight: '500px' }}
                    />
                  </div>
                  {images.slice(1, 5).map((img, idx) => (
                    <div key={idx} className="hidden md:block">
                      <OptimizedImage
                        src={img}
                        alt={imageAlt(idx + 1)}
                        width={400}
                        height={246}
                        loading="lazy"
                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ height: '246px' }}
                        onClick={() => setSelectedImage(idx + 1)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* About */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('offer.aboutTitle')}</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{description}</p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('offer.includedTitle')}</h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
                  {inclusionKeys.map((key) => (
                    <li key={key} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-700">{t(`offer.inclusions.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('offer.goodToKnowTitle')}</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-gray-700">
                    <strong>{t('offer.goodToKnow.meeting')}</strong> {t('offer.goodToKnow.meetingValue')}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>{t('offer.goodToKnow.language')}</strong> {t('offer.goodToKnow.languageValue')}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>{t('offer.goodToKnow.cancellation')}</strong> {t('offer.goodToKnow.cancellationValue')}
                  </p>
                </div>
              </section>

            </div>

            {/* RIGHT COLUMN — booking card (sticky on desktop, inline on mobile after gallery) */}
            <div
              className={`w-full flex-shrink-0 self-start order-1 lg:order-2 lg:sticky lg:top-28 transition-all duration-500 ease-out ${
                embedInlineBooking && bookingSideExpanded ? 'lg:w-1/2' : 'lg:w-1/3'
              }`}
            >
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-lg">
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold text-gray-900">
                      €{getDisplayTtcEuros(effectiveOffer)}
                    </span>
                    <span className="text-gray-600">
                      / {isPerPersonPricing ? t('offerCard.perPerson') : t('offerCard.perGroup')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold">{reviewStats.rating.toFixed(1)}</span>
                    <span className="text-gray-600">({reviewStats.review_count} reviews)</span>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <div>
                      <div className="text-sm text-gray-500">{t('offerCard.duration')}</div>
                      <div className="font-semibold">
                        {Number(effectiveOffer.duration_minutes ?? effectiveOffer.duration) > 0
                          ? formatDuration(Number(effectiveOffer.duration_minutes ?? effectiveOffer.duration))
                          : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Users className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <div>
                      <div className="text-sm text-gray-500">{t('offerCard.capacity')}</div>
                      <div className="font-semibold">{t('offerCard.upTo')} {effectiveOffer.max_capacity || 2} {t('offerCard.people')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Calendar className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <div>
                      <div className="text-sm text-gray-500">Type</div>
                      <div className="font-semibold">
                        {effectiveOffer.offer_type === 'tour' ? t('offerCard.guidedTour') : t('offerCard.selfGuided')}
                      </div>
                    </div>
                  </div>
                </div>

                {embedInlineBooking ? (
                  <BookingModal
                    key={effectiveOffer.id}
                    embedded
                    suppressBookingHeader
                    isOpen
                    onClose={() => {}}
                    offer={effectiveOffer}
                    onFirstInteraction={handleBookingFormInteract}
                    className="mt-2 pt-2 border-t border-gray-100"
                  />
                ) : (
                  <Button
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white py-6 text-lg font-semibold rounded-lg"
                    onClick={() => setIsBookingModalOpen(true)}
                  >
                    {t('nav.bookNow')}
                  </Button>
                )}

                <p className="text-xs text-gray-500 text-center mt-4">
                  {language === 'fr' ? "Annulation gratuite jusqu'à 24h avant" : language === 'es' ? 'Cancelación gratuita hasta 24h antes' : 'Free cancellation up to 24h before'}
                </p>
              </div>
            </div>

          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
      
      {!embedInlineBooking && (
        <BookingModal
          key={effectiveOffer.id}
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          offer={effectiveOffer}
        />
      )}
    </div>
  );
};

export default OfferDetailPage;
